import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Typography,
  Box,
  Alert,
  InputAdornment,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
// import { settleHotelInvoice } from "../../store/actions";
import { toast } from "react-toastify";
import { settleHotelInvoice } from "../../../store/actions";

export default function SettlementDialog({ open, onClose, invoice }) {
  const dispatch = useDispatch();

  // Get current user from Redux store
  const currentUser = useSelector((state) => state.auth?.user); // Adjust path based on your Redux structure

  const [formData, setFormData] = useState({
    amount: "",
    payment_mode: "cash",
    transaction_ref: "",
    settlement_date: new Date().toISOString().split("T")[0],
    remarks: "",
    settled_by: currentUser?.name || currentUser?.username || currentUser?.email || "",
  });

  const remainingBalance = invoice.total - (invoice.paid_amount || 0);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    const amount = parseFloat(formData.amount);

    // Validation
    if (!amount || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (amount > remainingBalance) {
      toast.error(
        `Amount cannot exceed balance due (₹${remainingBalance.toFixed(2)})`
      );
      return;
    }

    if (!formData.payment_mode) {
      toast.error("Please select a payment mode");
      return;
    }

    if (!formData.settlement_date) {
      toast.error("Please select a settlement date");
      return;
    }

    if (!formData.settled_by || formData.settled_by.trim() === "") {
      toast.error("Please enter who is settling this payment");
      return;
    }

    // Dispatch settlement action
    dispatch(
      settleHotelInvoice({
        invoice_id: invoice._id,
        settlement: {
          amount,
          payment_mode: formData.payment_mode,
          transaction_ref: formData.transaction_ref || undefined,
          settlement_date: Math.floor(
            new Date(formData.settlement_date).getTime() / 1000
          ),
          remarks: formData.remarks || undefined,
          settled_by: formData.settled_by.trim(),
        },
      })
    );

    onClose();

    // Reset form
    setFormData({
      amount: "",
      payment_mode: "cash",
      transaction_ref: "",
      settlement_date: new Date().toISOString().split("T")[0],
      remarks: "",
      settled_by: currentUser?.name || currentUser?.username || currentUser?.email || "",
    });
  };

  const handleSetFullAmount = () => {
    setFormData((prev) => ({
      ...prev,
      amount: remainingBalance.toFixed(2),
    }));
  };

  const handleCancel = () => {
    onClose();
    // Reset form
    setFormData({
      amount: "",
      payment_mode: "cash",
      transaction_ref: "",
      settlement_date: new Date().toISOString().split("T")[0],
      remarks: "",
      settled_by: currentUser?.name || currentUser?.username || currentUser?.email || "",
    });
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Settle Invoice #{invoice.inv_no}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Invoice Total: <strong>₹{invoice.total.toFixed(2)}</strong>
            </Typography>
            <Typography variant="body2">
              Already Paid:{" "}
              <strong>₹{(invoice.paid_amount || 0).toFixed(2)}</strong>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Balance Due:{" "}
              <strong style={{ fontSize: "1.1em" }}>
                ₹{remainingBalance.toFixed(2)}
              </strong>
            </Typography>
          </Alert>

          <Grid container spacing={2}>
            {/* Settlement Amount */}
            <Grid item xs={12}>
              <TextField
                label="Settlement Amount"
                type="number"
                value={formData.amount}
                onChange={(e) => handleChange("amount", e.target.value)}
                fullWidth
                required
                inputProps={{
                  min: 0.01,
                  max: remainingBalance,
                  step: 0.01,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                }}
                helperText={
                  <Button
                    size="small"
                    onClick={handleSetFullAmount}
                    sx={{
                      textTransform: "none",
                      p: 0,
                      minWidth: 0,
                      fontSize: "0.75rem",
                    }}
                  >
                    Click to use full balance (₹{remainingBalance.toFixed(2)})
                  </Button>
                }
              />
            </Grid>

            {/* Payment Mode */}
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Payment Mode</InputLabel>
                <Select
                  value={formData.payment_mode}
                  onChange={(e) => handleChange("payment_mode", e.target.value)}
                  label="Payment Mode"
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="card">Card</MenuItem>
                  <MenuItem value="upi">UPI</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  <MenuItem value="cheque">Cheque</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Transaction Reference */}
            <Grid item xs={12}>
              <TextField
                label="Transaction Reference (Optional)"
                value={formData.transaction_ref}
                onChange={(e) =>
                  handleChange("transaction_ref", e.target.value)
                }
                fullWidth
                placeholder="Card/UPI/Cheque Number"
                helperText="Optional: Enter transaction ID, card last 4 digits, or cheque number"
              />
            </Grid>

            {/* Settlement Date */}
            <Grid item xs={12}>
              <TextField
                label="Settlement Date"
                type="date"
                value={formData.settlement_date}
                onChange={(e) =>
                  handleChange("settlement_date", e.target.value)
                }
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Settled By */}
            <Grid item xs={12}>
              <TextField
                label="Settled By"
                value={formData.settled_by}
                onChange={(e) => handleChange("settled_by", e.target.value)}
                fullWidth
                required
                placeholder="Enter name of person settling this invoice"
                helperText="Name of the person recording this settlement"
              />
            </Grid>

            {/* Remarks */}
            <Grid item xs={12}>
              <TextField
                label="Remarks (Optional)"
                value={formData.remarks}
                onChange={(e) => handleChange("remarks", e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder="Add any notes about this settlement..."
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCancel} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            !formData.amount ||
            parseFloat(formData.amount) <= 0 ||
            !formData.settled_by ||
            formData.settled_by.trim() === ""
          }
        >
          Record Settlement
        </Button>
      </DialogActions>
    </Dialog>
  );
}